import { useState } from 'react';
import { Button, Modal, message } from 'antd';
import { Trash2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import PPPageHeader from '../../components/pageHeader/PPPageHeader';
import PPTableLink from '../../components/link/PPTableLink';
import ColumnSettings from '../../components/table/ColumnSettings';
import PPResizableTable from '../../components/table/PPResizableTable';
import { useColumnSettings } from '../../components/table/useColumnSettings';
import { isHandledByInterceptor } from '../../utils/apiError';
import { useTableFilter } from '../../utils/Filter';
import { getIndexColumn } from '../../utils/getIndexColumn';
import StatusBadge from './StatusBadge';
import './style.less';

const routes = [
  {
    breadcrumbName: 'Барааны удирдлага',
  },
  {
    breadcrumbName: 'Барааны өөрчлөлтийн хүсэлт',
  },
];

const getColumns = (
  location,
  getColumnSearchProps,
  getColumnFilterProps,
  pagination,
  handleDelete,
  statusEnum
) => [
  getIndexColumn(pagination.current, pagination.pageSize),
  {
    title: 'Барааны нэр',
    dataIndex: 'productTitle',
    key: 'productTitle',
    ...getColumnSearchProps('productTitle', 'Барааны нэр'),
    render: (text, record) => (
      <PPTableLink
        to={{
          pathname: `/product-changes/${record._id}`,
          state: { search: location.search },
        }}
      >
        {text}
      </PPTableLink>
    ),
    width: 200,
  },
  {
    title: 'SKU',
    dataIndex: 'productSku',
    key: 'productSku',
    ...getColumnSearchProps('productSku', 'SKU'),
    width: 200,
  },
  {
    title: 'Огноо',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (createdAt) => (createdAt ? dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'),
    width: 200,
  },
  {
    title: 'Үүсгэсэн хэрэглэгч',
    dataIndex: 'merchant',
    key: 'merchant',
    render: (merchant) => merchant || '-',
    width: 200,
  },
  {
    title: 'Хариулсан хэрэглэгч',
    dataIndex: 'reviewedBy',
    key: 'reviewedBy',
    render: (reviewedBy) => reviewedBy || '-',
    width: 200,
  },
  {
    title: 'Төлөв',
    dataIndex: 'status',
    key: 'status',
    filters: Object.entries(statusEnum ?? {}).map(([value, text]) => ({
      text,
      value,
    })),
    ...getColumnFilterProps('status'),
    render: (status) => <StatusBadge status={status} labels={statusEnum} />,
    width: 200,
  },
  {
    title: '',
    key: 'action',
    width: 60,
    align: 'center',
    fixed: 'right',
    render: (_, record) => (
      <Button type="text" danger icon={<Trash2 size={18} />} onClick={() => handleDelete(record)} />
    ),
  },
];

const ProductChanges = () => {
  const location = useLocation();
  const { confirm } = Modal;
  const [data, setData] = useState([]);
  const [statusEnum, setStatusEnum] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});

  const fetchData = async ({ page = 1, pageSize = 10, ...filters } = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/v1.0/product/listUpdateRequest', {
        page,
        pageSize,
        ...filters,
      });
      setData(data?.result?.docs || []);
      setStatusEnum(data?.result?.statusEnum || {});
      setPagination({
        current: data?.result?.page ?? page,
        pageSize: data?.result?.limit ?? pageSize,
        total: data?.result?.totalDocs ?? 0,
      });
    } catch (error) {
      // Алдааны мэдэгдлийг axios interceptor аль хэдийн гаргасан — давхардуулахгүй
      console.error('listUpdateRequest: ', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const { getColumnSearchProps, getColumnFilterProps, handleTableChange, withHighlight, filters } =
    useTableFilter({
      fetchData,
    });

  const handleDelete = (record) => {
    if (record.status !== 'pending') {
      message.error("Зөвхөн 'Хүсэлт үүсгэсэн' төлөвтэй хүсэлтийг устгах боломжтой.");
      return;
    }

    confirm({
      title: 'Хүсэлтийг устгах',
      content: `Та "${record.productTitle}" хүсэлтийг устгахдаа итгэлтэй байна уу?`,
      okText: 'Устгах',
      okType: 'danger',
      cancelText: 'Болих',
      async onOk() {
        try {
          setLoading(true);
          const { data } = await axios.delete('/api/v1.0/product/updateRequest', {
            params: { id: record._id },
          });
          // Алдааг interceptor мэдэгдсэн бол амжилттай гэж давхар харуулахгүй
          if (isHandledByInterceptor(data)) return;
          message.success('Хүсэлтийг амжилттай устгалаа.');
          fetchData({
            page: pagination.current,
            pageSize: pagination.pageSize,
            ...filters,
          });
        } catch (err) {
          // Алдааны мэдэгдлийг axios interceptor аль хэдийн гаргасан — давхардуулахгүй
          console.error('delete updateRequest: ', err);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const { columns, settingsProps, onColumnOrderChange } = useColumnSettings({
    columns: withHighlight(
      getColumns(
        location,
        getColumnSearchProps,
        getColumnFilterProps,
        pagination,
        handleDelete,
        statusEnum
      )
    ),
    storageKey: 'productChange.list',
  });

  return (
    <div>
      <PPPageHeader
        title="Барааны өөрчлөлтийн хүсэлт"
        breadcrumb={{ routes }}
        extra={<ColumnSettings {...settingsProps} />}
      />
      <div className="pp_table_wrapper">
        <PPResizableTable
          rowKey="_id"
          columns={columns}
          onColumnOrderChange={onColumnOrderChange}
          dataSource={data}
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Нийт ${total} илэрц`,
          }}
          size="middle"
          scroll={{ x: 1100 }}
        />
      </div>
    </div>
  );
};

export default ProductChanges;
